import { useMemo } from "react";
import { toDateKey } from "@mmm/shared";
import type { EventDto } from "@mmm/contracts";
import { EventChip } from "./EventChip.js";
import { IconButton } from "../ui/IconButton.js";
import { useLanguage, useT } from "@mmm/ui";
function getCalendarDays(year: number, month: number): Array<Date | null> {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days: Array<Date | null> = [];
  for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
  for (let day = 1; day <= lastDay.getDate(); day++) days.push(new Date(year, month, day));
  return days;
}

interface MonthGridProps {
  year: number;
  month: number;
  onMonthChange: (amount: number) => void;
  eventsByDate: Map<string, EventDto[]>;
  onEventClick?: (event: EventDto) => void;
  readOnly?: boolean;
}

export function MonthGrid({ year, month, onMonthChange, eventsByDate, onEventClick, readOnly = false }: MonthGridProps) {
  const t = useT();
  const { language } = useLanguage();
  const days = getCalendarDays(year, month);

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat(language, { month: "long", year: "numeric" }).format(new Date(year, month, 1)),
    [language, year, month]
  );

  // The grid's columns start on Sunday (getCalendarDays pads by getDay()), so
  // the headers are Sunday-first too; 2021-08-01 was a Sunday.
  const weekdayLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(language, { weekday: "short" });
    return Array.from({ length: 7 }, (_, i) => formatter.format(new Date(2021, 7, 1 + i)));
  }, [language]);

  return (
    <div className="mmm-month-grid">
      <div className="mmm-month-grid-head">
        <h2>{monthLabel}</h2>
        <div className="mmm-month-grid-nav">
          <IconButton icon="collapse" label={t("calendar.previousMonth")} onClick={() => onMonthChange(-1)} />
          <IconButton icon="expand" label={t("calendar.nextMonth")} onClick={() => onMonthChange(1)} />
        </div>
      </div>

      <div className="mmm-month-grid-days">
        {weekdayLabels.map((label, index) => (
          <div key={index} className="mmm-month-grid-weekday">
            {label}
          </div>
        ))}
        {days.map((day, index) => {
          const key = day ? toDateKey(day) : `blank-${index}`;
          const dayEvents = day ? eventsByDate.get(toDateKey(day)) || [] : [];
          return (
            <div
              key={key}
              className={`mmm-month-grid-cell ${day ? "" : "is-empty"}`.trim()}
            >
              {day && <div className="mmm-month-grid-date">{day.getDate()}</div>}
              {dayEvents.map((event) => (
                <EventChip key={event.id} event={event} readOnly={readOnly} onClick={() => onEventClick?.(event)} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
